<template>
    <div
        class="team-sheet"
        :class="{'team-sheet--expanded': expanded, 'team-sheet--dragging': isDragging}"
        :style="dragInlineStyle"
        @touchstart.passive="onTouchStart"
        @touchend="onTouchEnd"
    >
        <div
            class="team-sheet-handle"
            @pointerdown="onHandlePointerDown"
            @pointermove="onHandlePointerMove"
            @pointerup="onHandlePointerUp"
            @pointercancel="onHandlePointerCancel"
            @lostpointercapture="onHandlePointerCancel"
        >
            <div class="team-sheet-handle-bar"></div>
            <v-icon
                v-if="!expanded && hasUnreadPlayerMessages"
                color="#FFD700"
                size="18"
                class="team-sheet-peek-unread-icon"
            >mdi-email</v-icon>
            <v-icon
                v-if="!expanded && hasPvpMyTurn"
                color="#FFD700"
                size="18"
                class="team-sheet-peek-unread-icon team-sheet-peek-unread-icon--pvp"
            >mdi-sword-cross</v-icon>
            <div v-if="!expanded" class="team-sheet-peek">
                <template v-if="activeTab === 'team'">
                    <div
                        v-for="p in localPokemons"
                        :key="p.id"
                        class="team-sheet-peek-ring"
                        :style="{backgroundImage: hpRingGradient(p)}"
                    >
                        <div
                            class="team-sheet-peek-bg"
                            :style="{background: hpBgColor(p)}"
                        />
                        <v-img :src="pokemonIcon(p)" contain class="team-sheet-peek-sprite"/>
                    </div>
                    <span v-if="localPokemons.length === 0" class="team-sheet-peek-empty">{{ $t('journey.teamSheet.peek.yourTeam') }}</span>
                </template>
                <template v-else-if="activeTab === 'messages'">
                    <span v-if="lastMessage" class="team-sheet-peek-message">{{ lastMessage.text }}</span>
                    <span v-else class="team-sheet-peek-empty">{{ $t('journey.teamSheet.peek.noMessagesYet') }}</span>
                </template>
                <template v-else-if="activeTab === 'playerMessages'">
                    <span v-if="lastPlayerMessage" class="team-sheet-peek-message">{{ conversationSenderLabel(lastPlayerMessage) }}: {{ lastPlayerMessage.lastMessage.text }}</span>
                    <span v-else class="team-sheet-peek-empty">{{ $t('journey.teamSheet.peek.noConversationsYet') }}</span>
                </template>
                <template v-else-if="activeTab === 'pvp'">
                    <span v-if="lastPvpMatch" class="team-sheet-peek-message">{{ pvpOtherTrainer(lastPvpMatch).name }}: {{ pvpStatusLabel(lastPvpMatch) }}</span>
                    <span v-else class="team-sheet-peek-empty">{{ $t('journey.teamSheet.peek.noPvpMatchesYet') }}</span>
                </template>
                <template v-else-if="activeTab === 'trainer'">
                    <span v-if="ownTrainer" class="team-sheet-peek-message">{{ $t('journey.teamSheet.peek.level', {level: ownTrainer.experience.level}) }}{{ ownTrainerCityName ? ' · ' + ownTrainerCityName : '' }}</span>
                    <span v-else class="team-sheet-peek-empty">{{ $t('journey.teamSheet.peek.trainerInfoFallback') }}</span>
                </template>
                <template v-else-if="activeTab === 'achievements'">
                    <span v-if="ownTrainer" class="team-sheet-peek-message">{{ $t('journey.teamSheet.peek.achievementsMaxed', {count: maxedAchievesCount, total: achieves.length}) }}</span>
                    <span v-else class="team-sheet-peek-empty">{{ $t('trainer.trainerBag.tabs.achievements') }}</span>
                </template>
                <template v-else>
                    <span v-if="ownTrainer" class="team-sheet-peek-message">{{ $tc('journey.teamSheet.peek.freeSkillPoints', freeSkillpoints, {n: freeSkillpoints}) }}</span>
                    <span v-else class="team-sheet-peek-empty">{{ $t('trainer.trainerBag.skillsDialog.title') }}</span>
                </template>
            </div>
            <div v-else class="team-sheet-tabs-row">
                <draggable
                    v-model="tabs"
                    tag="div"
                    class="team-sheet-tabs-drag"
                    :delay="180"
                    delay-on-touch-only
                    animation="150"
                    @end="onTabReorder"
                >
                    <div
                        v-for="tab in tabs"
                        :key="tab.id"
                        class="team-sheet-tab-icon"
                        :class="{
                            'team-sheet-tab-icon--active': activeTab === tab.id,
                            'team-sheet-tab-icon--evolve': tab.id === 'team' && hasEvolvablePokemon,
                        }"
                        @click.stop="activeTab = tab.id"
                    >
                        <v-img v-if="tab.iconType === 'img'" :src="tab.icon" width="20" height="20" contain/>
                        <v-icon
                            v-else
                            :color="(tab.id === 'playerMessages' && hasUnreadPlayerMessages) || (tab.id === 'pvp' && hasPvpMyTurn) || (tab.id === 'skills' && freeSkillpoints > 0) ? '#FFD700' : 'white'"
                            size="20"
                        >{{ tab.icon }}</v-icon>
                    </div>
                </draggable>
                <v-icon color="white" class="ml-1">mdi-chevron-down</v-icon>
            </div>
        </div>
        <PlayerChatPopup
            v-model="chatOpenModel"
            :trainer="chatWithTrainer"
            :my-trainer-id="trainerId"
        />
        <ResponsiveDialog v-model="challengeDialogOpen" :max-width="360" :full-height="false" :header-title="$t('journey.teamSheet.challengeDialog.title')">
            <v-card-text v-if="challengeTrainer" class="text-center pa-4">
                <p>{{ $t('journey.teamSheet.challengeDialog.confirmPrefix') }} <strong>{{ challengeTrainer.name }}</strong> {{ $t('journey.teamSheet.challengeDialog.confirmSuffix') }}</p>
                <v-select
                    v-model="challengeMatchSize"
                    :items="challengeMatchSizeOptions"
                    :label="$t('journey.teamSheet.pokemonPerSide')"
                    outlined
                    dense
                    hide-details
                    class="mb-4"
                />
                <v-alert v-if="challengeError" type="error" dense class="text-left">{{ challengeError }}</v-alert>
                <v-btn color="primary black--text btn-shadow" :loading="challengeBusy" block @click="sendChallenge">
                    {{ $t('journey.teamSheet.challengeDialog.sendButton') }}
                </v-btn>
            </v-card-text>
        </ResponsiveDialog>
        <!-- Vlastní v-dialog místo ResponsiveDialog (vědomá výjimka z
             roadmap-mobile.md bodu 8, viz claude/components/team-sheet.md) -
             karta už sama zobrazuje hlavičku, ResponsiveDialog rámeček by byl
             duplicitní. PokemonManager místo PokemonDetail (zadání uživatele
             2026-09-02: teamboard je správcovský pohled, měl to být vždy
             manager - PokemonDetail sem historicky přinesl battle škálování,
             které tu nedávalo smysl). Manager si pokémona fetchuje sám podle
             id (pokemon-manager serializace se statsPoints atd.) a změny
             propaguje přes callbacky, viz onManagerPokemonUpdated a spol.
             V boji: itemy skryté, evolve/PUSTIT disabled, skilly read-only
             (in-fight prop, stejná pravidla jako dřívější allow-* gaty). -->
        <v-dialog v-model="pokemonDetailOpenModel" max-width="480" overlay-color="#000000" overlay-opacity="0.05">
            <div :class="$vuetify.theme.dark ? 'transparent08--dark' : 'transparent08'">
                <PokemonManager
                    v-if="selectedPokemon"
                    :pokemon-id="selectedPokemon.id"
                    :in-fight="isFightMode"
                    :stones="stones"
                    :items="effectItems"
                    :height-image="400"
                    :update-pokemon="onManagerPokemonUpdated"
                    :buy-skill="onManagerSkillBought"
                    :delete-pokemon-callback="onManagerPokemonDeleted"
                    class="fill-height"
                    @item-use-error="itemUseErrorMessage = $event"
                />
            </div>
        </v-dialog>
        <!-- position:fixed - stejny duvod jako u OakSkillHintPopup vyse
             (nutne na korenove urovni .team-sheet, ne uvnitr v-dialog, ktery
             behem tranzice muze mit vlastni transform). -->
        <OakSkillHintPopup
            v-if="itemUseErrorMessage"
            icon="mdi-alert"
            danger
            roomy
            :text="itemUseErrorMessage"
            @close="itemUseErrorMessage = null"
        />
        <div
            ref="teamSheetBody"
            class="team-sheet-body"
            :class="{'team-sheet-body--draggable': bodyDragEligible}"
            @pointerdown="onHandlePointerDown"
            @pointermove="onHandlePointerMove"
            @pointerup="onHandlePointerUp"
            @pointercancel="onHandlePointerCancel"
            @lostpointercapture="onHandlePointerCancel"
        >
            <template v-if="activeTab === 'team'">
                <draggable
                    v-model="localPokemons"
                    handle=".team-sheet-drag-handle"
                    animation="150"
                    @end="onReorder"
                >
                    <div v-for="p in localPokemons" :key="p.id" class="team-sheet-row">
                        <div
                            class="team-sheet-row-ring"
                            :style="{backgroundImage: hpRingGradient(p)}"
                            @click="openPokemonDetail(p)"
                        >
                            <div
                                class="team-sheet-row-bg"
                                :style="{background: hpBgColor(p)}"
                            />
                            <v-img :src="pokemonIcon(p)" contain class="team-sheet-row-sprite"/>
                        </div>
                        <!-- Jeden SVG element (zadani uzivatele 2026-08-28, prevzato z
                             PokemonPickerCard.vue::.picker-row-level) - kruh/hvezda/cislo
                             ve stejnem viewBoxu, skaluje se jako jeden celek. Puvodni
                             varianta (CSS pozadi + samostatny <v-icon> hvezda + <span>
                             cislo, obojí nezavisle centrovane pres position:absolute)
                             se na malem kolecku pri mensich rozlisenich vizualne
                             rozjizdela hvezdicku od cisla. -->
                        <svg class="team-sheet-level-badge" viewBox="0 0 24 24">
                            <defs>
                                <radialGradient :id="levelGradientId(p)" cx="30%" cy="30%" r="75%">
                                    <stop offset="0%" stop-color="#ffffff" />
                                    <stop offset="100%" stop-color="#494949" />
                                </radialGradient>
                            </defs>
                            <circle cx="12" cy="12" r="12" :fill="`url(#${levelGradientId(p)})`" />
                            <path
                                d="M12,1L14.53,8.52L22.46,8.6L16.09,13.33L18.47,20.9L12,16.3L5.53,20.9L7.91,13.33L1.54,8.6L9.47,8.52Z"
                                fill="#ffebbc"
                            />
                            <text x="12" y="16.3" text-anchor="middle" :font-size="levelFontSize(p)" class="team-sheet-level-badge-text">{{ p.level }}</text>
                        </svg>
                        <div class="team-sheet-info">
                            <div class="team-sheet-name">{{ p.name }}</div>
                            <PokemonProgressBar
                                v-if="p.evolveProgress !== undefined && p.evolveProgress !== null"
                                :percent="p.evolveProgress"
                                variant="evolve"
                                :height="2"
                                class="team-sheet-progress-bar"
                            />
                            <v-progress-linear
                                :value="hpPercent(p)"
                                height="9"
                                rounded
                                background-color="bar-hp-red"
                                color="bar-hp-green"
                            />
                            <PokemonProgressBar
                                v-if="p.levelProgress !== undefined && p.levelProgress !== null"
                                :percent="p.levelProgress"
                                variant="level"
                                :height="2"
                                class="team-sheet-progress-bar"
                            />
                        </div>
                        <div class="team-sheet-right">
                            <div class="team-sheet-right-stats">
                                <div class="team-sheet-exp-text">
                                    {{ p.pokemonExperience.freeExp }}
                                    <v-icon size="24" color="#fff">mdi-star-box</v-icon>
                                </div>
                            </div>
                            <!-- pointerdown na drag-handle je ignorovaný v
                                 onHandlePointerDown (ne .stop tady), aby ho
                                 zachytil SortableJS na <draggable> (delegace
                                 přes handle=".team-sheet-drag-handle"), ne
                                 souběžně i body-drag. -->
                            <v-icon
                                class="team-sheet-drag-handle"
                                size="22"
                                color="rgba(255,255,255,0.6)"
                            >mdi-drag</v-icon>
                        </div>
                    </div>
                </draggable>
                <div v-if="localPokemons.length === 0" class="team-sheet-empty pa-4 text-center grey--text">
                    {{ $t('journey.teamSheet.team.emptyTeam') }}
                </div>
            </template>
            <div v-else-if="activeTab === 'messages'" class="team-sheet-messages">
                <template v-if="infoMessages.length">
                    <div v-for="m in visibleInfoMessages" :key="m.id" class="team-sheet-row team-sheet-message-row">
                        <div class="team-sheet-message-time">{{ messageTime(m.created) }}</div>
                        <div class="team-sheet-message-text">{{ m.text }}</div>
                    </div>
                    <TeamSheetPagerNav :page="messagePage" :total="totalMessagePages" @prev="messagePage--" @next="messagePage++"/>
                </template>
                <div v-else class="team-sheet-empty pa-4 text-center grey--text">
                    {{ $t('journey.teamSheet.messages.empty') }}
                </div>
            </div>
            <div v-else-if="activeTab === 'playerMessages'" class="team-sheet-player-messages">
                <v-text-field
                    v-model="messageSearchQuery"
                    :placeholder="$t('journey.teamSheet.playerMessages.searchPlaceholder')"
                    dense
                    hide-details
                    dark
                    prepend-inner-icon="mdi-magnify"
                    class="team-sheet-search-field"
                    @input="onSearchInput"
                />
                <template v-if="messageSearchResults.length">
                    <div
                        v-for="t in visibleMessageSearchResults"
                        :key="t.id"
                        class="team-sheet-row team-sheet-conv-row team-sheet-conv-row--search"
                        @click="openChat({id: t.id, name: t.name, img: t.img})"
                    >
                        <div class="team-sheet-conv-avatar-wrap">
                            <v-img v-if="t.img" :src="require('@/assets/pic/trainer/' + t.img)" class="team-sheet-conv-avatar" position="top center"/>
                        </div>
                        <div class="team-sheet-conv-info">
                            <div class="team-sheet-conv-name">{{ t.name }}</div>
                        </div>
                    </div>
                    <TeamSheetPagerNav
                        :page="playerSearchPage"
                        :total="totalPlayerSearchPages"
                        @prev="playerSearchPage--"
                        @next="playerSearchPage++"
                    />
                </template>
                <template v-else>
                    <div
                        v-for="c in visiblePlayerConversations"
                        :key="c.otherTrainer.id"
                        class="team-sheet-row team-sheet-conv-row"
                        :class="{'team-sheet-conv-row--unread': c.unread}"
                        @click="openChat(c.otherTrainer)"
                    >
                        <div class="team-sheet-conv-avatar-outer">
                            <div class="team-sheet-conv-avatar-wrap">
                                <v-img v-if="c.otherTrainer.img" :src="require('@/assets/pic/trainer/' + c.otherTrainer.img)" class="team-sheet-conv-avatar" position="top center"/>
                            </div>
                            <span v-if="c.otherTrainer.isOnline" class="team-sheet-conv-online-dot"/>
                        </div>
                        <div class="team-sheet-conv-info">
                            <div class="team-sheet-conv-name">{{ c.otherTrainer.name }}</div>
                            <div class="team-sheet-conv-preview">{{ conversationSenderLabel(c) }}: {{ c.lastMessage.text }}</div>
                        </div>
                    </div>
                    <TeamSheetPagerNav
                        v-if="playerConversations.length"
                        :page="playerMessagesPage"
                        :total="totalPlayerMessagesPages"
                        @prev="playerMessagesPage--"
                        @next="playerMessagesPage++"
                    />
                    <div v-if="playerConversations.length === 0" class="team-sheet-empty pa-4 text-center grey--text">
                        {{ $t('journey.teamSheet.playerMessages.empty') }}
                    </div>
                </template>
            </div>
            <div v-else-if="activeTab === 'pvp'" class="team-sheet-player-messages">
                <v-text-field
                    v-model="pvpSearchQuery"
                    :placeholder="$t('journey.teamSheet.pvp.searchPlaceholder')"
                    dense
                    hide-details
                    dark
                    prepend-inner-icon="mdi-magnify"
                    class="team-sheet-search-field"
                    @input="onPvpSearchInput"
                />
                <template v-if="pvpSearchResults.length">
                    <div
                        v-for="t in visiblePvpSearchResults"
                        :key="t.id"
                        class="team-sheet-row team-sheet-conv-row team-sheet-conv-row--search"
                        @click="openChallengeDialog(t)"
                    >
                        <div class="team-sheet-conv-avatar-wrap">
                            <v-img v-if="t.img" :src="require('@/assets/pic/trainer/' + t.img)" class="team-sheet-conv-avatar" position="top center"/>
                        </div>
                        <div class="team-sheet-conv-info">
                            <div class="team-sheet-conv-name">{{ t.name }}</div>
                        </div>
                    </div>
                    <TeamSheetPagerNav
                        :page="pvpSearchPage"
                        :total="totalPvpSearchPages"
                        @prev="pvpSearchPage--"
                        @next="pvpSearchPage++"
                    />
                </template>
                <template v-else>
                    <div
                        v-for="m in visibleSortedPvpMatches"
                        :key="m.id"
                        class="team-sheet-row team-sheet-conv-row"
                        :class="{'team-sheet-conv-row--active': isOpenPvpMatch(m), 'team-sheet-conv-row--unread': m.isMyTurn}"
                        @click="openPvpMatch(m)"
                    >
                        <div class="team-sheet-conv-avatar-outer">
                            <div class="team-sheet-conv-avatar-wrap">
                                <v-img v-if="pvpOtherTrainer(m).img" :src="require('@/assets/pic/trainer/' + pvpOtherTrainer(m).img)" class="team-sheet-conv-avatar" position="top center"/>
                            </div>
                        </div>
                        <div class="team-sheet-conv-info">
                            <div class="team-sheet-conv-name">{{ pvpOtherTrainer(m).name }}</div>
                            <div class="team-sheet-conv-preview">{{ pvpStatusLabel(m) }}</div>
                        </div>
                        <v-icon v-if="m.isMyTurn" color="#FFD700" size="18">mdi-sword-cross</v-icon>
                    </div>
                    <TeamSheetPagerNav
                        v-if="pvpMatches.length"
                        :page="pvpPage"
                        :total="totalPvpPages"
                        @prev="pvpPage--"
                        @next="pvpPage++"
                    />
                    <div v-if="pvpMatches.length === 0" class="team-sheet-empty pa-4 text-center grey--text">
                        {{ $t('journey.teamSheet.pvp.empty') }}
                    </div>
                </template>
            </div>
            <div v-else-if="activeTab === 'pvpRanked'" class="team-sheet-player-messages">
                <div
                    v-for="m in visibleSortedPvpRankedMatches"
                    :key="m.id"
                    class="team-sheet-row team-sheet-conv-row"
                    :class="{'team-sheet-conv-row--active': isOpenPvpRankedMatch(m), 'team-sheet-conv-row--unread': isPvpRankedWaitingOnMe(m)}"
                    @click="openPvpRankedMatch(m)"
                >
                    <div class="team-sheet-conv-avatar-outer">
                        <div class="team-sheet-conv-avatar-wrap">
                            <v-img v-if="pvpOtherTrainer(m).img" :src="require('@/assets/pic/trainer/' + pvpOtherTrainer(m).img)" class="team-sheet-conv-avatar" position="top center"/>
                        </div>
                    </div>
                    <div class="team-sheet-conv-info">
                        <div class="team-sheet-conv-name">{{ pvpOtherTrainer(m).name }}</div>
                        <div class="team-sheet-conv-preview">{{ pvpRankedStatusLabel(m) }}</div>
                    </div>
                    <v-icon v-if="isPvpRankedWaitingOnMe(m)" color="#FFD700" size="18">mdi-boxing-glove</v-icon>
                </div>
                <TeamSheetPagerNav
                    v-if="pvpRankedMatches.length"
                    :page="pvpRankedPage"
                    :total="totalPvpRankedPages"
                    @prev="pvpRankedPage--"
                    @next="pvpRankedPage++"
                />
                <div v-if="pvpRankedMatches.length === 0" class="team-sheet-empty pa-4 text-center grey--text">
                    {{ $t('journey.teamSheet.pvpRanked.empty') }}
                </div>
            </div>
            <div v-else-if="activeTab === 'trainer'" class="team-sheet-trainer">
                <template v-if="ownTrainer">
                    <div v-if="ownTrainer.fullTitle" class="team-sheet-trainer-title">{{ ownTrainer.fullTitle }}</div>

                    <table class="team-sheet-trainer-info-table">
                        <tbody>
                        <tr>
                            <td>{{ $t('trainer.trainerList.level') }}</td>
                            <td>{{ ownTrainer.experience.level }}</td>
                        </tr>
                        <tr>
                            <td>{{ $t('trainer.trainerList.position') }}</td>
                            <td>{{ ownTrainer.position && ownTrainer.position.city ? $t('common.cityNames.' + ownTrainer.position.city.name) : $t('journey.teamSheet.trainerInfo.choosingPokemon') }}</td>
                        </tr>
                        <tr>
                            <td>{{ $t('trainer.trainerList.nextMilestone') }}</td>
                            <td>{{ ownTrainer.position && ownTrainer.position.nextCity ? $t('common.cityNames.' + ownTrainer.position.nextCity.name) : $t('trainer.trainerList.lostCity') }}</td>
                        </tr>
                        <tr>
                            <td>{{ $t('journey.teamSheet.trainerInfo.pokeballs') }}</td>
                            <td><v-icon size="14" color="white" class="team-sheet-trainer-info-icon">mdi-pokeball</v-icon>{{ ownTrainer.pokeballs }}</td>
                        </tr>
                        <tr>
                            <td>{{ $t('journey.teamSheet.trainerInfo.masterballs') }}</td>
                            <td><v-icon size="14" color="white" class="team-sheet-trainer-info-icon">mdi-baseball</v-icon>{{ ownTrainer.masterballs }}</td>
                        </tr>
                        <tr>
                            <td>{{ $t('journey.teamSheet.trainerInfo.activeTeam') }}</td>
                            <td>{{ ownTrainer.travellingPokemonsCount }}</td>
                        </tr>
                        <tr>
                            <td>{{ $t('journey.teamSheet.trainerInfo.pokemonsTotal') }}</td>
                            <td>{{ ownTrainer.havingPokemonsCount }}</td>
                        </tr>
                        <tr v-if="ownTrainer.stolenPokemonsCount">
                            <td>{{ $t('journey.teamSheet.trainerInfo.stolenByRocket') }}</td>
                            <td>{{ ownTrainer.stolenPokemonsCount }}</td>
                        </tr>
                        <tr>
                            <td>{{ $t('journey.teamSheet.trainerInfo.trainerWins') }}</td>
                            <td>{{ ownTrainer.wins }}</td>
                        </tr>
                        </tbody>
                    </table>

                    <div v-if="ownTrainer.badges && ownTrainer.badges.length" class="team-sheet-trainer-badges">
                        <v-img
                            v-for="badge in ownTrainer.badges"
                            :key="badge.id"
                            :src="require(`@/assets/pic/badge/${badge.id}.png`)"
                            class="team-sheet-trainer-badge-icon"
                            contain
                        />
                    </div>
                </template>
                <div v-else class="team-sheet-empty pa-4 text-center grey--text">
                    {{ $t('journey.teamSheet.trainerInfo.loading') }}
                </div>
            </div>
            <div v-else-if="activeTab === 'achievements'" class="team-sheet-trainer">
                <template v-if="ownTrainer">
                    <div class="team-sheet-trainer-achievements">
                        <div
                            v-for="item in achieves"
                            :key="item.id"
                            class="team-sheet-trainer-achieve-row"
                            :class="{'team-sheet-trainer-achieve-row--active': openAchieveInfo === item.id}"
                            @click="toggleAchieveInfo(item.id)"
                        >
                            <span class="team-sheet-trainer-achieve-name">{{ item.name }}</span>
                            <span
                                v-if="item.lvl !== null && item.lvl !== undefined"
                                class="team-sheet-trainer-achieve-stars"
                            >
                                <v-icon
                                    v-for="n in item.lvl"
                                    :key="'f'+n"
                                    size="14"
                                    color="#FFD700"
                                >mdi-star</v-icon>
                                <v-icon
                                    v-for="n in (item.maxLvl - item.lvl)"
                                    :key="'e'+n"
                                    size="14"
                                    color="rgba(255,255,255,0.3)"
                                >mdi-star-outline</v-icon>
                            </span>
                            <span class="team-sheet-trainer-achieve-value">
                                <template v-if="item.next !== null && item.next !== undefined">{{ item.value }}/{{ item.next }}</template>
                                <template v-else>{{ item.value }}</template>
                            </span>

                            <OakSkillHintPopup
                                v-if="openAchieveInfo === item.id"
                                :skill="{name: item.name, description: item.messages.join(' ')}"
                                @close="openAchieveInfo = null"
                                @touchstart.native.stop
                                @touchend.native.stop
                                @pointerdown.native.stop
                            />
                        </div>
                    </div>
                </template>
                <div v-else class="team-sheet-empty pa-4 text-center grey--text">
                    {{ $t('journey.teamSheet.trainerInfo.loading') }}
                </div>
            </div>
            <div v-else-if="activeTab === 'skills'" class="team-sheet-trainer">
                <template v-if="ownTrainer">
                    <div class="team-sheet-trainer-skills-summary">
                        {{ $tc('journey.teamSheet.peek.freeSkillPoints', freeSkillpoints, {n: freeSkillpoints}) }}
                    </div>
                    <div class="team-sheet-trainer-skills">
                        <div
                            v-for="(skill, key) in trainerSkillsConfig"
                            :key="key"
                            class="team-sheet-trainer-skill-row"
                            :class="{'team-sheet-trainer-skill-row--active': openTrainerSkillInfo === key}"
                            @click="toggleTrainerSkillInfo(key)"
                        >
                            <v-icon size="18" :color="canBuySkill(key) ? '#FFD700' : 'white'">{{ skill.icon }}</v-icon>
                            <span class="team-sheet-trainer-skill-name">{{ $t('trainer.trainerSkills.' + key + '.name') }}</span>
                            <span class="team-sheet-trainer-skill-value">{{ ownTrainer.trainerSkills[key] }}</span>
                            <span
                                class="team-sheet-trainer-skill-buy"
                                :class="{'team-sheet-trainer-skill-buy--left': rightHanded}"
                                @click.stop
                            >
                                <v-btn
                                    :loading="isLoading"
                                    :disabled="!canBuySkill(key)"
                                    x-small
                                    rounded
                                    class="skin-btn-buy"
                                    @click.stop="buySkill(key)"
                                >
                                    {{ $t('trainer.trainerBag.skillsDialog.buyButton') }}
                                </v-btn>
                            </span>

                            <!-- OakSkillHintPopup je position:fixed bez vlastniho
                                 teleportu, takze zustava vnoreny pod korenovym
                                 .team-sheet (swipe-mezi-zalozkami + body-drag
                                 listenery) - .native.stop brani gestu uvnitr
                                 popupu prepnout zalozku/spustit body-drag. -->
                            <OakSkillHintPopup
                                v-if="openTrainerSkillInfo === key"
                                :skill="{icon: skill.icon, name: $t('trainer.trainerSkills.' + key + '.name'), description: $t('trainer.trainerSkills.' + key + '.description')}"
                                @close="openTrainerSkillInfo = null"
                                @touchstart.native.stop
                                @touchend.native.stop
                                @pointerdown.native.stop
                            />
                        </div>
                    </div>
                </template>
                <div v-else class="team-sheet-empty pa-4 text-center grey--text">
                    {{ $t('journey.teamSheet.trainerInfo.loading') }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    // Globální singleton (mountovaný v App.vue, position:fixed) - viz
    // claude/components/team-sheet.md pro designové rozhodnutí.
    import {mapActions, mapGetters} from 'vuex';
    import draggable from 'vuedraggable';
    import {pokemonImageSrc} from '@/utils/pokemonImage';
    import PlayerChatPopup from '@/components/journey/next/pieces/PlayerChatPopup';
    import PokemonProgressBar from '@/components/pokemon/PokemonProgressBar';
    import PokemonManager from '@/components/pokemon/PokemonManager';
    import OakSkillHintPopup from '@/components/utils/OakSkillHintPopup';
    import ResponsiveDialog from '@/components/utils/ResponsiveDialog';
    import TeamSheetPagerNav from '@/components/journey/next/pieces/TeamSheetPagerNav';
    import {TRAINER_SKILLS} from '@/utils/trainerSkillsEnum';
    import {flags, setFlag, syncFlagsFromBackend} from '@/config/featureFlags';
    import mercureClient from '@/utils/mercureClient';
    import {consumePvpSelfAction} from '@/utils/pvpSelfActionTracker';
    import {playNotificationSound} from '@/utils/notificationAudio';
    import {messageTime} from '@/utils/formatTime';

    // TEAM_SHEET_EXPANDED_HEIGHT musi zustat v souladu s CSS .team-sheet--expanded
    // nize (46px handle + 6 x 58px radek pokemona + 12px padding .team-sheet-body).
    const TEAM_SHEET_COLLAPSED_HEIGHT = 46;
    const TEAM_SHEET_EXPANDED_HEIGHT = 46 + 6 * 58 + 12;
    const TEAM_SHEET_DRAG_DEADZONE = 8;
    // Taby bez vlastniho scrollovani - jen tam smi drag fungovat pres cele
    // telo, ne jen pres handle (viz bodyDragEligible/measureFittingRowCount).
    const TEAM_SHEET_BODY_DRAG_TABS = ['team', 'trainer', 'achievements', 'skills', 'messages', 'playerMessages', 'pvp', 'pvpRanked'];
    // Fallback pred prvnim uspesnym zmerenim (viz measureFittingRowCount) -
    // odvozeno stejnym vypoctem jako 6 radku tymu (348px, viz
    // TEAM_SHEET_EXPANDED_HEIGHT), radek 29px = 12 radku presne.
    const TEAM_SHEET_MESSAGE_ROW_HEIGHT = 29;
    const TEAM_SHEET_MAX_VISIBLE_MESSAGES = Math.floor(
        (TEAM_SHEET_EXPANDED_HEIGHT - TEAM_SHEET_COLLAPSED_HEIGHT - 12) / TEAM_SHEET_MESSAGE_ROW_HEIGHT
    );
    // Fallback pred prvnim zmerenim/pri chybe mereni pro playerMessages/pvp -
    // odhad ze znamych CSS rozmeru (avatar 36px + row padding 12px = 48px/
    // radek, vyhledavaci pole ~48px, nav radek se sipkami 29px).
    const TEAM_SHEET_CONV_ROW_CAP_FALLBACK = Math.max(1, Math.floor(
        (TEAM_SHEET_EXPANDED_HEIGHT - TEAM_SHEET_COLLAPSED_HEIGHT - 12 - 48 - 29) / 48
    ));

    export default {
        name: 'TeamBottomSheet',
        components: {draggable, PlayerChatPopup, PokemonManager, OakSkillHintPopup, PokemonProgressBar, ResponsiveDialog, TeamSheetPagerNav},
        data() {
            return {
                expanded: false,
                localPokemons: [],
                activeTab: 'team',
                touchStartX: null,
                dragActive: false,
                dragOrigin: null,
                dragStartX: null,
                dragStartY: null,
                dragStartHeight: null,
                dragCurrentHeight: null,
                isDragging: false,
                suppressNextSwipe: false,
                axisLocked: null,
                messagePage: 0,
                measuredMessagePageSize: null,
                measuredConvRowCap: null,
                playerMessagesPage: 0,
                playerSearchPage: 0,
                pvpPage: 0,
                pvpSearchPage: 0,
                pvpRankedPage: 0,
                tabs: [
                    {id: 'team', iconType: 'img', icon: require('@/assets/pic/web/pokeball.png')},
                    {id: 'trainer', iconType: 'mdi', icon: 'mdi-account-cowboy-hat'},
                    {id: 'achievements', iconType: 'mdi', icon: 'mdi-trophy'},
                    {id: 'skills', iconType: 'mdi', icon: 'mdi-medal'},
                    {id: 'messages', iconType: 'mdi', icon: 'mdi-message-alert-outline'},
                    {id: 'playerMessages', iconType: 'mdi', icon: 'mdi-email-outline'},
                    {id: 'pvp', iconType: 'mdi', icon: 'mdi-sword-cross'},
                    {id: 'pvpRanked', iconType: 'mdi', icon: 'mdi-boxing-glove'},
                ],
                playerConversations: [],
                messageSearchQuery: '',
                messageSearchResults: [],
                searchDebounceTimer: null,
                chatOpen: false,
                chatWithTrainer: null,
                pvpSearchQuery: '',
                pvpSearchResults: [],
                pvpSearchDebounceTimer: null,
                allPlayers: [],
                challengeDialogOpen: false,
                challengeTrainer: null,
                challengeMatchSize: 1,
                challengeBusy: false,
                challengeError: '',
                pokemonDetailOpen: false,
                selectedPokemonId: null,
                itemUseErrorMessage: null,
                openTrainerSkillInfo: null,
                openAchieveInfo: null,
                trainerSkillsConfig: TRAINER_SKILLS,
                unsubscribePlayerMessage: null,
                unsubscribePvpUpdate: null,
                unsubscribePvpRankedUpdate: null,
            }
        },
        created() {
            this.applyStoredTabOrder();
            this._tabOrderChangedLocally = false;
            // created() běží dřív, než stihne doběhnout App.vue fetch
            // syncFlagsFromBackend(), takže první applyStoredTabOrder() výše
            // často čte ještě nesynchronizovanou localStorage cache - proto
            // druhý reapply po dokončení fetch. Guard _tabOrderChangedLocally
            // brání přepsání uživatelova mezitímního reorderu zastaralou
            // odpovědí (viz onTabReorder níže).
            syncFlagsFromBackend().then(() => {
                if (!this._tabOrderChangedLocally) {
                    this.applyStoredTabOrder();
                }
            });
            this.unsubscribePlayerMessage = mercureClient.onMessage('player_message', this.onPlayerMessageEvent);
            this.unsubscribePvpUpdate = mercureClient.onMessage('pvp_update', this.onPvpUpdateEvent);
            this.unsubscribePvpRankedUpdate = mercureClient.onMessage('pvp_ranked_update', this.onPvpRankedUpdateEvent);
            this.remeasureRows();
        },
        beforeDestroy() {
            if (this.unsubscribePlayerMessage) {
                this.unsubscribePlayerMessage();
            }
            if (this.unsubscribePvpUpdate) {
                this.unsubscribePvpUpdate();
            }
            if (this.unsubscribePvpRankedUpdate) {
                this.unsubscribePvpRankedUpdate();
            }
        },
        computed: {
            ...mapGetters({
                pokemons: 'pokemon/items',
                user: 'user/item',
                infoMessages: 'trainer/infoMessages',
                isFightMode: 'journey/isFightMode',
                isLoading: 'trainer/isLoading',
                buttonVolume: 'journey/buttonVolume',
                // ownTrainer je oddělený store slot od trainer/item (ten sdílejí pohledy na cizí trenéry).
                ownTrainer: 'trainer/ownTrainer',
                freeSkillpoints: 'trainer/freeSkillpoints',
                pvpMatches: 'trainer/pvpMatches',
                pvpRankedMatches: 'trainer/pvpRankedList',
            }),
            rightHanded() {
                return flags.rightHanded;
            },
            hasEvolvablePokemon() {
                return this.localPokemons.some((p) => p.evolvable && p.evolvable.length > 0);
            },
            // "id" je stabilni identifikator pro v-for/porovnavani (viz openAchieveInfo), "name"/"messages" jsou uz prelozene $t() - musi byt computed.
            // Stejna data jako TrainerBag.vue achieves - klice sdilene, needuplikovat (trainer.trainerBag.achievements.*).
            achieves() {
                if (!this.ownTrainer) {
                    return [];
                }
                const t = this.ownTrainer;
                const reachNextLvlHint = this.$t('trainer.trainerBag.achievements.reachNextLvlHint');
                const pokemonCatcherHint = this.$t('trainer.trainerBag.achievements.pokemonCatcherHint');
                let achieves = [];
                achieves.push({
                    id: 'pokemonObserver',
                    name: this.$t('trainer.trainerBag.achievements.pokemonObserver.name'),
                    value: t.pokedex.length,
                    next: t.nextPokedexLvl,
                    lvl: t.pokedexLvl,
                    maxLvl: t.pokedexMaxLvl,
                    messages: [this.$t('trainer.trainerBag.achievements.pokemonObserver.hint'), reachNextLvlHint]
                });
                if (t.leagueChampion) {
                    achieves.push({
                        id: 'alolanPokemonObserver',
                        name: this.$t('trainer.trainerBag.achievements.alolanPokemonObserver.name'),
                        value: t.pokedexAlola.length,
                        next: t.nextPokedexLvlAlola,
                        lvl: t.pokedexLvlAlola,
                        maxLvl: t.pokedexMaxLvlAlola,
                        messages: [this.$t('trainer.trainerBag.achievements.alolanPokemonObserver.hint'), reachNextLvlHint]
                    });
                }
                achieves.push({
                    id: 'pokemonCatcherGen1',
                    name: this.$t('trainer.trainerBag.achievements.pokemonCatcherGen1.name'),
                    value: t.catchedTypesGen1.length,
                    next: t.nextCatchedPokemonTypesLvlGen1,
                    lvl: t.pokemonTypesAchieveLvlGen1,
                    maxLvl: t.pokemonTypesAchieveMaxLvlGen1,
                    messages: [pokemonCatcherHint, reachNextLvlHint]
                });
                if (t.leagueChampion) {
                    achieves.push({
                        id: 'pokemonCatcherAlola',
                        name: this.$t('trainer.trainerBag.achievements.pokemonCatcherAlola.name'),
                        value: t.catchedTypesAlola.length,
                        next: t.nextCatchedPokemonTypesLvlAlola,
                        lvl: t.pokemonTypesAchieveLvlAlola,
                        maxLvl: t.pokemonTypesAchieveMaxLvlAlola,
                        messages: [pokemonCatcherHint, reachNextLvlHint]
                    });
                }
                achieves.push({
                    id: 'trainerDevastator',
                    name: this.$t('trainer.trainerBag.achievements.trainerDevastator.name'),
                    value: t.trainerdex.length,
                    next: t.nextTrainerdexLvl,
                    lvl: t.trainerdexLvl,
                    maxLvl: t.trainerdexMaxLvl,
                    messages: [this.$t('trainer.trainerBag.achievements.trainerDevastator.hint'), reachNextLvlHint]
                });
                achieves.push({
                    id: 'trainerFightVictories',
                    name: this.$t('trainer.trainerBag.achievements.trainerFightVictories.name'),
                    value: t.wins,
                    next: null,
                    lvl: null,
                    maxLvl: null,
                    messages: [this.$t('trainer.trainerBag.achievements.trainerFightVictories.hint')]
                });
                return achieves;
            },
            maxedAchievesCount() {
                return this.achieves.filter(item => item.maxLvl !== null && item.maxLvl !== undefined && item.lvl >= item.maxLvl).length;
            },
            ownTrainerCityName() {
                return this.ownTrainer && this.ownTrainer.position && this.ownTrainer.position.city
                    ? this.$t('common.cityNames.' + this.ownTrainer.position.city.name)
                    : '';
            },
            // Eevee evolve větev v PokemonDetail potřebuje evoluční kameny (stones prop) - dřív sem
            // vůbec nechodily (ownTrainer nebylo po ruce), viz claude/components/team-sheet.md.
            // Stejná normalizace (items jako object vs. array) a filtr jako TrainerDetail.vue::getStones().
            stones() {
                if (!this.ownTrainer || !this.ownTrainer.items) {
                    return [];
                }
                let items = this.ownTrainer.items;
                if (typeof items === 'object' && items !== null && !Array.isArray(items)) {
                    items = Object.keys(items).map((key) => items[key]);
                }
                return items.filter((item) => item.category === 'EVOLUTION STONE');
            },
            // Vse mimo evolucni kameny (permanentni i spotrebni itemy) - pro
            // UseItemButton v PokemonDetail, stejna filtrace jako
            // TrainerDetail.vue::getEffectItems(). Sdili normalizaci i zdroj
            // dat (ownTrainer.items) se stones computed vyse.
            effectItems() {
                if (!this.ownTrainer || !this.ownTrainer.items) {
                    return [];
                }
                let items = this.ownTrainer.items;
                if (typeof items === 'object' && items !== null && !Array.isArray(items)) {
                    items = Object.keys(items).map((key) => items[key]);
                }
                return items.filter((item) => item.category !== 'EVOLUTION STONE');
            },
            // Ne přímo z pokemons getteru - po nákupu skillu se localPokemons
            // přepíše watcherem níže, tenhle computed se s tím automaticky
            // přepočítá i když popup zůstane otevřený.
            selectedPokemon() {
                return this.localPokemons.find((p) => p.id === this.selectedPokemonId) || null;
            },
            trainerId() {
                return this.user ? this.user.id : null;
            },
            lastMessage() {
                return this.infoMessages.length ? this.infoMessages[0] : null;
            },
            lastPlayerMessage() {
                return this.playerConversations.length ? this.playerConversations[0] : null;
            },
            lastPvpMatch() {
                return this.sortedPvpMatches.length ? this.sortedPvpMatches[0] : null;
            },
            // Lokální computed místo Vuex getteru journey/
            // trainerHasUnreadPlayerMessages - ten se neobnovoval na živou
            // Mercure událost (position se nenačítá znovu), takže žlutý
            // indikátor po příchozí zprávě nenaskočil hned.
            hasUnreadPlayerMessages() {
                return this.playerConversations.some((c) => c.unread);
            },
            hasPvpMyTurn() {
                return this.pvpMatches.some((m) => m.isMyTurn);
            },
            // Stabilní sort zachová sekundární řazení z backendu (created DESC).
            sortedPvpMatches() {
                return [...this.pvpMatches].sort((a, b) => this.pvpSortBucket(a) - this.pvpSortBucket(b));
            },
            // Select nesmí nabízet víc pokémonů na stranu, než kolik jich
            // soupeř skutečně má (jinak by výzvu nemohl přijmout - viz
            // choosePokemon guard v PvpService, claude/game-rules/pvp.md).
            challengeMatchSizeOptions() {
                const opponentCount = this.challengeTrainer && this.challengeTrainer.pokemons
                    ? this.challengeTrainer.pokemons.length
                    : 6;
                const max = Math.max(1, Math.min(6, opponentCount));
                return Array.from({length: max}, (_, i) => i + 1);
            },
            // v-model se side-efektem - zavření popupu obnoví konverzace a
            // vynutí refresh position payloadu, ať zlatá ikonka zmizí hned.
            chatOpenModel: {
                get() {
                    return this.chatOpen;
                },
                set(open) {
                    this.chatOpen = open;
                    if (!open) {
                        this.closeChat();
                    }
                },
            },
            // Zavření popupu vynuluje selectedPokemonId, takže `v-if` v šabloně
            // při každém dalším otevření PokemonDetail odmountuje a znovu
            // namountuje komponentu (ne recyklace instance se starým stavem).
            pokemonDetailOpenModel: {
                get() {
                    return this.pokemonDetailOpen;
                },
                set(open) {
                    this.pokemonDetailOpen = open;
                    if (!open) {
                        this.selectedPokemonId = null;
                    }
                },
            },
            // Behem live-draggingu prebiji CSS height inline stylem (transition
            // je docasne vypnuta pres .team-sheet--dragging, viz styl nize).
            dragInlineStyle() {
                return this.isDragging && this.dragCurrentHeight !== null
                    ? {height: this.dragCurrentHeight + 'px'}
                    : {};
            },
            // Panel musí být rozbalený - na zavřeném stavu tělo není vidět.
            bodyDragEligible() {
                return this.expanded && TEAM_SHEET_BODY_DRAG_TABS.includes(this.activeTab);
            },
            messagePageSize() {
                return this.measuredMessagePageSize || TEAM_SHEET_MAX_VISIBLE_MESSAGES;
            },
            totalMessagePages() {
                return Math.max(1, Math.ceil(this.infoMessages.length / this.messagePageSize));
            },
            // infoMessages dorazi ze serveru uz razene od nejnovejsi (viz
            // GetInfoMessagesWithNewAction findBy created=desc) - stranka 0 =
            // nejnovejsi napřed.
            visibleInfoMessages() {
                const start = this.messagePage * this.messagePageSize;
                return this.infoMessages.slice(start, start + this.messagePageSize);
            },
            convRowCap() {
                return this.measuredConvRowCap || TEAM_SHEET_CONV_ROW_CAP_FALLBACK;
            },
            totalPlayerMessagesPages() {
                return Math.max(1, Math.ceil(this.playerConversations.length / this.convRowCap));
            },
            visiblePlayerConversations() {
                const start = this.playerMessagesPage * this.convRowCap;
                return this.playerConversations.slice(start, start + this.convRowCap);
            },
            totalPlayerSearchPages() {
                return Math.max(1, Math.ceil(this.messageSearchResults.length / this.convRowCap));
            },
            visibleMessageSearchResults() {
                const start = this.playerSearchPage * this.convRowCap;
                return this.messageSearchResults.slice(start, start + this.convRowCap);
            },
            totalPvpPages() {
                return Math.max(1, Math.ceil(this.sortedPvpMatches.length / this.convRowCap));
            },
            visibleSortedPvpMatches() {
                const start = this.pvpPage * this.convRowCap;
                return this.sortedPvpMatches.slice(start, start + this.convRowCap);
            },
            totalPvpSearchPages() {
                return Math.max(1, Math.ceil(this.pvpSearchResults.length / this.convRowCap));
            },
            visiblePvpSearchResults() {
                const start = this.pvpSearchPage * this.convRowCap;
                return this.pvpSearchResults.slice(start, start + this.convRowCap);
            },
            totalPvpRankedPages() {
                return Math.max(1, Math.ceil(this.pvpRankedMatches.length / this.convRowCap));
            },
            visibleSortedPvpRankedMatches() {
                const sorted = [...this.pvpRankedMatches].sort((a, b) => this.pvpRankedSortBucket(a) - this.pvpRankedSortBucket(b));
                const start = this.pvpRankedPage * this.convRowCap;
                return sorted.slice(start, start + this.convRowCap);
            },
        },
        watch: {
            // Kdyz vybrany pokemon zmizi z cerstve nactenych localPokemons
            // (evoluce/uvolneni/novy den jinde v appce), popup by jinak
            // zustal viset otevreny s prazdnym obsahem (PokemonDetail se
            // odmountuje diky v-if, ale v-dialog sam o sobe ne).
            selectedPokemon(value) {
                if (!value) {
                    this.pokemonDetailOpen = false;
                }
            },
            // Bez tohohle by po odchodu ze zalozky Skills/Trener a navratu
            // zpet vyskocil naposledy otevreny Oak hint popup sam od sebe,
            // i kdyz ho uzivatel mezitim nezavrel rucne.
            activeTab(tab) {
                if (tab !== 'skills') {
                    this.openTrainerSkillInfo = null;
                }
                if (tab !== 'achievements') {
                    this.openAchieveInfo = null;
                }
                if (tab === 'messages') {
                    this.messagePage = 0;
                }
                if (tab === 'playerMessages') {
                    this.playerMessagesPage = 0;
                    this.playerSearchPage = 0;
                }
                if (tab === 'pvp') {
                    this.pvpPage = 0;
                    this.pvpSearchPage = 0;
                }
                if (tab === 'pvpRanked') {
                    this.pvpRankedPage = 0;
                }
                this.remeasureRows();
            },
            expanded(value) {
                if (value) {
                    // Čekáme na doběhnutí 0.22s CSS transition (viz
                    // .team-sheet--expanded) - okamžitý remeasureRows() by
                    // změřil ještě rozjíždějící se, ne finální výšku.
                    setTimeout(() => {
                        this.remeasureRows();
                    }, 250);
                }
            },
            infoMessages() {
                this.remeasureRows();
                this.messagePage = Math.min(this.messagePage, this.totalMessagePages - 1);
            },
            // Živé události/refetch NEMAJÍ resetovat rozjetou stránku cizího
            // původu (např. nová příchozí zpráva od jiného hráče) - jen clamp.
            playerConversations() {
                this.remeasureRows();
                this.playerMessagesPage = Math.min(this.playerMessagesPage, this.totalPlayerMessagesPages - 1);
            },
            // Nové výsledky hledání (každý stisk klávesy) naopak vždy začínají
            // na první stránce.
            messageSearchResults() {
                this.playerSearchPage = 0;
            },
            pvpMatches() {
                this.remeasureRows();
                this.pvpPage = Math.min(this.pvpPage, this.totalPvpPages - 1);
            },
            pvpSearchResults() {
                this.pvpSearchPage = 0;
            },
            pvpRankedMatches() {
                this.remeasureRows();
                this.pvpRankedPage = Math.min(this.pvpRankedPage, this.totalPvpRankedPages - 1);
            },
            trainerId: {
                immediate: true,
                handler(id) {
                    if (id) {
                        this.loadTeam();
                        this.loadPlayerConversations();
                        this.loadPvpMatchesList();
                        this.loadPvpRankedMatchesList();
                        this.loadOwnTrainerAction(id);
                        this.loadAllPlayersAction().then((players) => {
                            this.allPlayers = players || [];
                        });
                        // Sheet je globální a viditelná hned po přihlášení,
                        // takže si musí infoMessages natáhnout sama - jiné
                        // Journey obrazovky je jinak volají samy ve svém
                        // created(), ale to by tu nestačilo.
                        this.trainerInfoMessages(id);
                    }
                },
            },
            // Behem tazeni v-model mění jen localPokemons (jiná reference
            // pole), takže se tenhle watcher nespustí - přepíše se, až
            // dorazí čerstvá data ze serveru.
            pokemons: {
                immediate: true,
                handler(list) {
                    this.localPokemons = (list || []).slice();
                },
            },
            // Sheet je globální nad router-view, takže by při otevírání PvP
            // zápasu z rozbaleného stavu zbytečně překrývala čerstvě otevřený
            // fight.
            '$route.name'(name) {
                if (name === 'PvpBattleScreen') {
                    this.expanded = false;
                }
            },
        },
        methods: {
            ...mapActions({
                loadActiveTeam: 'pokemon/loadActiveTeam',
                updatePokemon: 'pokemon/updateItem',
                refreshActiveTeamAction: 'pokemon/refreshActiveTeam',
                searchTrainerByNameAction: 'trainer/searchTrainerByName',
                loadPlayerConversationsAction: 'trainer/loadPlayerConversations',
                loadPosition: 'journey/loadPosition',
                loadOwnTrainerAction: 'trainer/loadOwnTrainer',
                buyTrainerSkillAction: 'trainer/buyTrainerSkill',
                loadPvpMatchesAction: 'trainer/loadPvpMatches',
                loadPvpRankedListAction: 'trainer/loadPvpRankedList',
                challengePvpAction: 'trainer/challengePvp',
                loadAllPlayersAction: 'trainer/loadAllPlayers',
                trainerInfoMessages: 'trainer/loadInfoMessages',
            }),
            async onReorder() {
                const base = Date.now();
                await Promise.all(this.localPokemons.map((p, index) =>
                    this.updatePokemon({id: p.id, edited: new Date(base + index * 1000)})
                ));
            },
            // Změří skutečnou dostupnou výšku .team-sheet-body a výšku
            // prvního vykresleného řádku (rowSelector), spočítá kolik řádků
            // se vejde bez ořiznutí. reserveSelectors (nepovinné pole) jsou
            // elementy, jejichž výška + margin-bottom se odečte od dostupné
            // plochy dřív (např. vyhledávací pole a řádek se šipkami u
            // playerMessages/pvp). Vrací null, pokud se nemá z čeho počítat -
            // volající v tom případě drží fallback konstantu.
            measureFittingRowCount(bodyEl, rowSelector, reserveSelectors) {
                if (!bodyEl || !bodyEl.clientHeight) {
                    return null;
                }
                const rowEl = bodyEl.querySelector(rowSelector);
                if (!rowEl || !rowEl.offsetHeight) {
                    return null;
                }
                let available = bodyEl.clientHeight;
                (reserveSelectors || []).forEach((selector) => {
                    const reserveEl = bodyEl.querySelector(selector);
                    if (reserveEl) {
                        const marginBottom = parseFloat(window.getComputedStyle(reserveEl).marginBottom) || 0;
                        available -= reserveEl.offsetHeight + marginBottom;
                    }
                });
                // Nikdy nevracet vymyšlenou "1" jen proto, že dostupná plocha
                // vyšla záporná/malá (např. měřeno během ještě běžící CSS
                // transition) - bez flex-grow na .team-sheet-body by to bylo
                // trvalé, sebe-posilující. Chyba měření -> null, volající
                // podrží předchozí/fallback hodnotu.
                const rows = Math.floor(available / rowEl.offsetHeight);
                return rows >= 1 ? rows : null;
            },
            // Přepočítá se po každém překreslení, které může změnit dostupnou
            // plochu nebo výšku řádku (přepnutí tabu, rozbalení panelu, nová
            // data). $nextTick, aby už byl v DOM aktuální počet řádků.
            remeasureRows() {
                if (!this.expanded) {
                    return;
                }
                this.$nextTick(() => {
                    const body = this.$refs.teamSheetBody;
                    if (!body) {
                        return;
                    }
                    if (this.activeTab === 'messages') {
                        const size = this.measureFittingRowCount(body, '.team-sheet-message-row', ['.team-sheet-page-nav']);
                        if (size) {
                            this.measuredMessagePageSize = size;
                            // Přeměření může zvětšit počet řádků na stránku,
                            // čímž se totalMessagePages zmenší - bez ořezání
                            // by messagePage zůstal ukazovat za konec pole.
                            this.messagePage = Math.min(this.messagePage, this.totalMessagePages - 1);
                        }
                    } else if (this.activeTab === 'playerMessages' || this.activeTab === 'pvp' || this.activeTab === 'pvpRanked') {
                        // Vybírá výhradně konverzační/zápasové řádky (ne
                        // výsledky hledání, které jsou nižší - jednořádkové).
                        const cap = this.measureFittingRowCount(body, '.team-sheet-conv-row:not(.team-sheet-conv-row--search)', ['.team-sheet-search-field', '.team-sheet-page-nav']);
                        if (cap) {
                            this.measuredConvRowCap = cap;
                            // Cap se může i zvětšit (fallback -> naměřeno víc),
                            // což sníží počet stran - bez clampu by čítadlo
                            // mohlo zůstat za koncem. Vyhledávací čítadla clamp
                            // nepotřebují, ta se resetují na 0 při každé změně
                            // výsledků.
                            this.playerMessagesPage = Math.min(this.playerMessagesPage, this.totalPlayerMessagesPages - 1);
                            this.pvpPage = Math.min(this.pvpPage, this.totalPvpPages - 1);
                            this.pvpRankedPage = Math.min(this.pvpRankedPage, this.totalPvpRankedPages - 1);
                        }
                    }
                });
            },
            // Neznámá ID ve uloženém pořadí se ignorují, ID chybějící v
            // uloženém pořadí (nový tab přidaný později) se připojí na konec.
            applyStoredTabOrder() {
                const stored = (flags.tabOrder || '').split(',').filter(Boolean);
                if (!stored.length) {
                    return;
                }
                const byId = new Map(this.tabs.map((t) => [t.id, t]));
                const ordered = stored.map((id) => byId.get(id)).filter(Boolean);
                const orderedIds = new Set(ordered.map((t) => t.id));
                const remaining = this.tabs.filter((t) => !orderedIds.has(t.id));
                this.tabs = [...ordered, ...remaining];
            },
            onTabReorder() {
                this._tabOrderChangedLocally = true;
                setFlag('tabOrder', this.tabs.map((t) => t.id).join(','));
            },
            // Pointer capture zajistí, že pointerup/pointermove dorazí i když
            // prst/kurzor během tažení opustí handle. Filtr na e.button
            // vyřazuje pravý/prostřední klik u myši.
            onHandlePointerDown(e) {
                if (e.pointerType === 'mouse' && e.button !== 0) {
                    return;
                }
                const fromBody = e.currentTarget.classList.contains('team-sheet-body');
                if (fromBody && !this.bodyDragEligible) {
                    return;
                }
                if (fromBody && e.target.closest && e.target.closest('.team-sheet-drag-handle')) {
                    return;
                }
                // Tab-ikonky jsou uvnitř .team-sheet-handle - bez guardu by
                // pointerdown/up bublal do tap-toggle stavového automatu a
                // sbaloval/rozbaloval panel místo jen vlastního @click.
                if (!fromBody && e.target.closest && e.target.closest('.team-sheet-tab-icon')) {
                    return;
                }
                this.dragActive = true;
                this.dragOrigin = fromBody ? 'body' : 'handle';
                this.isDragging = false;
                this.axisLocked = null;
                this.dragStartX = e.clientX;
                this.dragStartY = e.clientY;
                this.dragStartHeight = this.expanded ? TEAM_SHEET_EXPANDED_HEIGHT : TEAM_SHEET_COLLAPSED_HEIGHT;
                this.dragCurrentHeight = this.dragStartHeight;
                if (e.target.setPointerCapture) {
                    e.target.setPointerCapture(e.pointerId);
                }
            },
            // Axis-lock: první překročení deadzone rozhodne SMĚR gesta (podle
            // toho, který delta byl v tu chvíli větší) a gesto se tohodle
            // rozhodnutí drží až do puštění. Vodorovný vítěz ukončí
            // pointer-drag a nechá gesto dorazit k touch-event swipe
            // handleru na rootu (viz onTouchStart/onTouchEnd níže).
            onHandlePointerMove(e) {
                if (!this.dragActive) {
                    return;
                }
                const deltaX = this.dragStartX - e.clientX;
                const deltaY = this.dragStartY - e.clientY;
                if (this.axisLocked === null) {
                    if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < TEAM_SHEET_DRAG_DEADZONE) {
                        return;
                    }
                    this.axisLocked = Math.abs(deltaY) > Math.abs(deltaX) ? 'y' : 'x';
                    if (this.axisLocked === 'x') {
                        this.dragActive = false;
                        if (e.target.releasePointerCapture) {
                            e.target.releasePointerCapture(e.pointerId);
                        }
                        return;
                    }
                    this.isDragging = true;
                }
                if (!this.isDragging) {
                    return;
                }
                this.dragCurrentHeight = Math.min(
                    TEAM_SHEET_EXPANDED_HEIGHT,
                    Math.max(TEAM_SHEET_COLLAPSED_HEIGHT, this.dragStartHeight + deltaY)
                );
            },
            // Pod deadzone je to tap - chová se přesně jako původní @click.
            // Nad deadzone živý drag skončí snapem na 25% prahu z otevřené
            // výšky a potlačí následující horizontální swipe.
            onHandlePointerUp(e) {
                if (!this.dragActive) {
                    return;
                }
                this.dragActive = false;
                this.axisLocked = null;
                if (e.target.releasePointerCapture) {
                    e.target.releasePointerCapture(e.pointerId);
                }
                if (!this.isDragging) {
                    // Tap-toggle jen když gesto začalo na handle - tap na
                    // těle (např. otevření PokemonDetail) nesmí navíc
                    // zavřít/otevřít panel.
                    if (this.dragOrigin === 'handle') {
                        this.expanded = !this.expanded;
                    }
                    this.dragOrigin = null;
                    return;
                }
                this.isDragging = false;
                // Pointer eventy stříli PŘED kompatibilními touch eventy pro
                // stejné gesto - root onTouchEnd tenhle flag stihne přečíst
                // dřív, než vyhodnotí horizontální swipe (diagonální tažení
                // by jinak mohlo omylem přepnout i tab).
                this.suppressNextSwipe = true;
                const threshold = 0.25 * TEAM_SHEET_EXPANDED_HEIGHT;
                if (this.dragStartHeight === TEAM_SHEET_COLLAPSED_HEIGHT) {
                    this.expanded = (this.dragCurrentHeight - TEAM_SHEET_COLLAPSED_HEIGHT) >= threshold;
                } else {
                    this.expanded = (TEAM_SHEET_EXPANDED_HEIGHT - this.dragCurrentHeight) < threshold;
                }
                this.dragCurrentHeight = null;
                this.dragOrigin = null;
            },
            onHandlePointerCancel(e) {
                if (!this.dragActive) {
                    return;
                }
                this.dragActive = false;
                this.isDragging = false;
                this.axisLocked = null;
                this.dragOrigin = null;
                this.dragCurrentHeight = null;
                if (e.target.releasePointerCapture) {
                    e.target.releasePointerCapture(e.pointerId);
                }
            },
            loadTeam() {
                this.loadActiveTeam(this.trainerId);
            },
            canBuySkill(key) {
                return this.ownTrainer
                    && this.freeSkillpoints > 0
                    && this.ownTrainer.trainerSkills[key] < this.ownTrainer.experience.level;
            },
            buySkill(key) {
                this.buyTrainerSkillAction({trainerId: this.ownTrainer.id, skillName: key}).then((success) => {
                    if (success && this.ownTrainer.position && this.ownTrainer.position.id) {
                        this.loadPosition(this.ownTrainer.position.id);
                    }
                });
            },
            openPokemonDetail(pokemon) {
                this.selectedPokemonId = pokemon.id;
                this.pokemonDetailOpen = true;
            },
            // Propagace zmen z PokemonManager popupu do sdileneho
            // pokemon/items store (HP po pouziti itemu, skillIds/freeExp po
            // nakupu skillu, evoluce) - manager narozdil od PokemonDetail
            // pracuje jen lokalne a zmeny hlasi pres callbacky, store patch
            // musi udelat volajici. refreshActiveTeam s patchem = lokalni
            // patch itemu, bez patche = plny refetch tymu.
            onManagerPokemonUpdated(data) {
                // sendEvolvePokemon posila primo pokemona, evolveEevee cely
                // response objekt {pokemon: ...}, onItemUsed nic (useItem
                // vraci jen trenerova data, viz komentar v PokemonManageru).
                const patch = data && data.pokemon ? data.pokemon : data;
                this.refreshActiveTeamAction(patch && patch.id ? patch : null);
            },
            onManagerSkillBought(pokemon) {
                this.refreshActiveTeamAction(pokemon && pokemon.id ? pokemon : null);
            },
            onManagerPokemonDeleted() {
                // Plny refetch - pokemon zmizel z tymu, selectedPokemon
                // watcher nahore pak popup sam zavre.
                this.refreshActiveTeamAction(null);
            },
            toggleTrainerSkillInfo(key) {
                this.openTrainerSkillInfo = this.openTrainerSkillInfo === key ? null : key;
            },
            toggleAchieveInfo(name) {
                this.openAchieveInfo = this.openAchieveInfo === name ? null : name;
            },
            loadPlayerConversations() {
                this.loadPlayerConversationsAction(this.trainerId).then((data) => {
                    this.playerConversations = data || [];
                });
            },
            conversationSenderLabel(c) {
                return c.lastMessage.senderId === this.trainerId ? this.$t('journey.toplistTable.youLabel') : c.otherTrainer.name;
            },
            // Payload z Mercure nese kompletní data zprávy, žádný další fetch
            // netřeba.
            onPlayerMessageEvent(payload) {
                const senderId = payload.sender.id;
                const updatedConversation = {
                    otherTrainer: payload.sender,
                    lastMessage: payload.message,
                    unread: true,
                };
                const idx = this.playerConversations.findIndex((c) => c.otherTrainer.id === senderId);
                if (idx >= 0) {
                    this.playerConversations.splice(idx, 1, updatedConversation);
                } else {
                    this.playerConversations.unshift(updatedConversation);
                }
                this.playerConversations.sort((a, b) => new Date(b.lastMessage.created) - new Date(a.lastMessage.created));
            },
            onSearchInput() {
                clearTimeout(this.searchDebounceTimer);
                const query = this.messageSearchQuery.trim();
                if (query.length < 2) {
                    this.messageSearchResults = [];
                    return;
                }
                this.searchDebounceTimer = setTimeout(() => {
                    this.searchTrainerByNameAction(query).then((results) => {
                        this.messageSearchResults = (results || []).filter((t) => t.id !== this.trainerId);
                    });
                }, 300);
            },
            openChat(trainer) {
                this.chatWithTrainer = trainer;
                this.chatOpen = true;
                this.messageSearchQuery = '';
                this.messageSearchResults = [];
            },
            closeChat() {
                this.chatWithTrainer = null;
                this.loadPlayerConversations();
                if (this.user && this.user.position && this.user.position.id) {
                    this.loadPosition(this.user.position.id);
                }
            },
            loadPvpMatchesList() {
                this.loadPvpMatchesAction();
            },
            loadPvpRankedMatchesList() {
                this.loadPvpRankedListAction();
            },
            // Zvuk na každou změnu stavu PvP zápasu - dřívější přístup
            // (diff isMyTurn před/po refreshi) pokrýval jen část přechodů,
            // např. picknutí pokémona soupeřem nemění isMyTurn. Místo toho
            // každý pvp_update push, který není výsledkem VLASTNÍ akce (viz
            // pvpSelfActionTracker.js - markPvpSelfAction se volá při každé
            // PvP mutaci na FE), spustí zvuk - notify() posílá push oběma
            // hráčům, i tomu, kdo právě sám odehrál tah.
            onPvpUpdateEvent(payload) {
                const matchId = payload && payload.matchId !== undefined ? String(payload.matchId) : null;
                const wasSelf = matchId !== null && consumePvpSelfAction(matchId);
                this.loadPvpMatchesAction().then(() => {
                    if (wasSelf || !this.user || this.user.notificationsEnabled === false) {
                        return;
                    }
                    // Neupozorňovat na zápas, který hráč právě sleduje na
                    // obrazovce - porovnání proti aktuální route.
                    const openMatchId = this.$route.name === 'PvpBattleScreen' ? String(this.$route.params.id) : null;
                    if (matchId !== null && matchId === openMatchId) {
                        return;
                    }
                    this.playPvpNotificationSound();
                });
            },
            // Zrcadli onPvpUpdateEvent() - self-action potlaceni pres
            // 'ranked:' prefixovany klic (viz proposePvpRanked/atd. v
            // actions.js), stejny boxing-bell zvuk jako volne PvP (zadani
            // uzivatele 2026-08-26 - "prozatim tomu nech stejny zvukovy
            // efekt jako pro klasiku pvp"). Zamerne NEreseni "uz otevreno" -
            // LEVEL_PROPOSED vyzva nema vlastni obrazovku k porovnani proti
            // route a dohledavani jen pro ACTIVE/matchId pripad by bylo vic
            // slozitosti, nez tenhle ukol vyzaduje.
            onPvpRankedUpdateEvent(payload) {
                const challengeId = payload && payload.challengeId !== undefined ? String(payload.challengeId) : null;
                const wasSelf = challengeId !== null && consumePvpSelfAction('ranked:' + challengeId);
                this.loadPvpRankedListAction().then(() => {
                    if (wasSelf || !this.user || this.user.notificationsEnabled === false) {
                        return;
                    }
                    this.playPvpNotificationSound();
                });
            },
            playPvpNotificationSound() {
                // Výzva/tah v PvP hraje vždy boxing bell, ne uživatelův
                // "klasický" notifikační zvuk - tematicky specifické pro
                // souboj, ne obecná notifikace. Řídí se přepínačem
                // "Notifications" (viz gate výše), ne "Sounds" sliderem
                // (buttonVolume) - to je samostatná věc (efekty/kliky), proto
                // vždy na plnou.
                playNotificationSound('boxing-bell.wav', 1);
            },
            // Filtrování client-side - allPlayers je natažený jednou při
            // přihlášení (viz trainerId watcher), žádný další HTTP request
            // na stisknutí klávesy.
            onPvpSearchInput() {
                const query = this.pvpSearchQuery.trim().toLowerCase();
                if (query.length < 2) {
                    this.pvpSearchResults = [];
                    return;
                }
                this.pvpSearchResults = this.allPlayers.filter((t) =>
                    t.id !== this.trainerId && t.name.toLowerCase().indexOf(query) !== -1
                );
            },
            // Zadani uzivatele 2026-08-19 - "melo by vyskocit ze uz je
            // vyzvano" - drivejsi chovani otevrelo dialog a teprve po
            // Odeslat vyzvu spadlo na cerveny error z backendoveho unique
            // guardu (viz PvpService::challenge()). Teď se to zkontroluje
            // proti uz nactenym pvpMatches (zadny extra request).
            openChallengeDialog(trainer) {
                const existing = this.pvpMatches.find((m) =>
                    m.otherTrainer.id === trainer.id && (m.status === 'PENDING' || m.status === 'ACTIVE')
                );
                if (existing) {
                    this.$toast.info(this.$t('journey.teamSheet.pvp.ongoingMatchToast', {name: trainer.name}), {
                        timeout: 5000,
                        closeOnClick: true,
                        hideProgressBar: true,
                        closeButton: 'button',
                        icon: 'v-icon notranslate ml-0 pl-0 mdi mdi-sword-cross theme--dark',
                        onClick: () => this.openPvpMatch(existing),
                    });
                    return;
                }
                this.challengeTrainer = trainer;
                this.challengeMatchSize = Math.min(1, trainer.pokemons ? trainer.pokemons.length : 1) || 1;
                this.challengeError = '';
                this.challengeDialogOpen = true;
                this.pvpSearchQuery = '';
                this.pvpSearchResults = [];
            },
            sendChallenge() {
                this.challengeBusy = true;
                this.challengeError = '';
                this.challengePvpAction({receiverId: this.challengeTrainer.id, matchSize: this.challengeMatchSize})
                    // Odeslání výzvy nenaviguje na PvpBattleScreen, jen obnoví
                    // seznam - kliknutí na existující (i přijatou) výzvu v
                    // seznamu pořád naviguje normálně (viz openPvpMatch).
                    .then(() => {
                        this.challengeBusy = false;
                        this.challengeDialogOpen = false;
                        this.loadPvpMatchesList();
                        this.$toast.success(this.$t('journey.teamSheet.pvp.challengeSentToast'), {timeout: 3000, hideProgressBar: true});
                    })
                    .catch((e) => {
                        this.challengeBusy = false;
                        this.challengeError = e.message;
                    });
            },
            pvpSortBucket(m) {
                if (m.isMyTurn) {
                    return 0;
                }
                if (m.status === 'FINISHED' || m.status === 'DECLINED') {
                    return 2;
                }
                return 1;
            },
            // $router.push na stejnou route/params vyhazuje NavigationDuplicated -
            // pokud už v souboji jsme, místo navigace se sheet jen zavře.
            isOpenPvpMatch(m) {
                return this.$route.name === 'PvpBattleScreen' && String(this.$route.params.id) === String(m.id);
            },
            openPvpMatch(m) {
                if (this.isOpenPvpMatch(m)) {
                    this.expanded = false;
                    return;
                }
                this.$router.push({name: 'PvpBattleScreen', params: {id: m.id}});
            },
            pvpOtherTrainer(m) {
                return m.otherTrainer;
            },
            pvpStatusLabel(m) {
                if (m.status === 'PENDING') {
                    return m.isChallenger
                        ? this.$t('journey.teamSheet.pvp.status.waitingForAccept')
                        : this.$t('journey.teamSheet.pvp.status.challengesYou');
                }
                if (m.status === 'DECLINED') {
                    return this.$t('journey.teamSheet.pvp.status.declined');
                }
                if (m.status === 'FINISHED') {
                    if (m.iWon === null) {
                        return this.$t('journey.teamSheet.pvp.status.finished');
                    }
                    return m.iWon ? this.$t('journey.teamSheet.pvp.status.won') : this.$t('journey.teamSheet.pvp.status.lost');
                }
                return m.isMyTurn
                    ? this.$t('journey.teamSheet.pvp.status.yourTurn')
                    : this.$t('journey.teamSheet.pvp.status.waitingForOpponent');
            },
            // "Ceka se na muj tah" ekvivalent volneho PvP isMyTurn - ranked
            // polozky maji jen LEVEL_PROPOSED/ACTIVE/FINISHED/DECLINED status,
            // zadny per-kolo priznak jako u probihajici bitvy, takze zluta
            // znacka je jen pro nezodpovezeny navrh urceny mne.
            isPvpRankedWaitingOnMe(m) {
                return m.status === 'LEVEL_PROPOSED' && !m.isMyProposal;
            },
            pvpRankedSortBucket(m) {
                if (this.isPvpRankedWaitingOnMe(m)) {
                    return 0;
                }
                if (m.status === 'FINISHED' || m.status === 'DECLINED') {
                    return 2;
                }
                return 1;
            },
            // LEVEL_PROPOSED zapasy jeste nemaji matchId (PvpMatch vznika az v
            // accept()) - misto otevirani vlastniho vyjednavaciho dialogu tady
            // (byla by to druha kopie te logiky vedle PvpHub.vue) naviguje na
            // uz existujici /pvp/ranked deep-link, kde se vyjednavaci popup da
            // otevrit kliknutim na radek.
            isOpenPvpRankedMatch(m) {
                return m.matchId !== null && m.matchId !== undefined
                    && this.$route.name === 'PvpBattleScreen' && String(this.$route.params.id) === String(m.matchId);
            },
            openPvpRankedMatch(m) {
                if (this.isOpenPvpRankedMatch(m)) {
                    this.expanded = false;
                    return;
                }
                if (m.matchId !== null && m.matchId !== undefined) {
                    this.$router.push({name: 'PvpBattleScreen', params: {id: m.matchId}});
                } else {
                    this.$router.push({name: 'PvpRankedHub'});
                }
            },
            pvpRankedStatusLabel(m) {
                if (m.status === 'LEVEL_PROPOSED') {
                    return m.isMyProposal
                        ? this.$t('pvp.ranked.statusProposedByMe', {level: m.proposedLevel})
                        : this.$t('pvp.ranked.statusProposedByOpponent', {level: m.proposedLevel});
                }
                if (m.status === 'ACTIVE') {
                    return this.$t('pvp.ranked.statusActive');
                }
                if (m.status === 'DECLINED') {
                    return this.$t('pvp.ranked.statusDeclined');
                }
                if (m.iWon === null) {
                    return this.$t('pvp.hub.statusFinishedDraw');
                }
                return m.iWon ? this.$t('pvp.common.won') : this.$t('pvp.battleScreen.lost');
            },
            pokemonIcon(p) {
                return pokemonImageSrc(p.pokemonType.img);
            },
            hpPercent(p) {
                return parseInt(p.hp) / parseInt(p.maxHp) * 100;
            },
            // Font-size cisla levelu v SVG viewBox 0-24 jednotkach (ne px) -
            // hodnoty odladěné tak, aby se tučné číslo vešlo do kruhu bez
            // přetečení mimo hvězdičku kolem (stejné jako PokemonPickerCard.vue).
            levelFontSize(p) {
                const len = String(p.level).length;
                if (len <= 1) return 14;
                if (len === 2) return 11;
                return 8;
            },
            // Vic radku (pokemonu) v seznamu = vic instanci SVG <radialGradient> -
            // p.id zaruci unikatni id, jinak by vsechny <circle fill="url(#...)">
            // odkazovaly na tu UPLNE PRVNI <radialGradient> v DOM.
            levelGradientId(p) {
                return 'team-sheet-level-grad-' + p.id;
            },
            // Conic-gradient přímo podle skutečného HP procenta (ne stupňovité
            // prahy) - stejný poměr zelená/červená jako v normálním HP baru,
            // jen bez jeho texturovaného gradientu (na tenkém prstenci by
            // nešel rozumně použít).
            hpRingGradient(p) {
                const pct = Math.max(0, Math.min(100, this.hpPercent(p)));
                return `conic-gradient(#43A047 0% ${pct}%, #E53935 ${pct}% 100%)`;
            },
            hpBgColor(p) {
                return p.endangered ? '#E53935' : '#737373';
            },
            messageTime,
            // Listener na kořenovém .team-sheet (ne jen handle baru), takže
            // swipe funguje nad celým obsahem. Nekoliduje s draggable
            // pokemon-listem v tab `team` (handle=".team-sheet-drag-handle"
            // omezuje SortableJS jen na tenhle prvek) ani s draggable
            // tab-ikonkami díky guardu níže.
            onTouchStart(e) {
                if (e.target.closest && e.target.closest('.team-sheet-tab-icon')) {
                    // SortableJS na tab-ikonkách má převzít gesto celé -
                    // touchStartX=null nechá onTouchEnd bezpečně bailnout.
                    this.touchStartX = null;
                    this.suppressNextSwipe = false;
                    return;
                }
                this.suppressNextSwipe = false;
                this.touchStartX = e.changedTouches[0].clientX;
            },
            onTouchEnd(e) {
                if (this.suppressNextSwipe) {
                    // Právě proběhl vertikální drag na handle - i kdyby gesto
                    // mělo i vodorovnou složku, nechceme přepnout tab.
                    this.suppressNextSwipe = false;
                    this.touchStartX = null;
                    return;
                }
                if (this.touchStartX === null) {
                    return;
                }
                const deltaX = e.changedTouches[0].clientX - this.touchStartX;
                this.touchStartX = null;
                if (Math.abs(deltaX) < 40) {
                    return;
                }
                e.preventDefault();
                const currentIndex = this.tabs.findIndex(t => t.id === this.activeTab);
                const step = deltaX < 0 ? 1 : -1;
                // Wrap na obou koncích - "+ this.tabs.length" před modulo,
                // protože JS "%" vrací záporné číslo pro záporný zbytek.
                const nextIndex = (currentIndex + step + this.tabs.length) % this.tabs.length;
                this.activeTab = this.tabs[nextIndex].id;
            },
        },
    }
</script>

<style scoped>
.team-sheet {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    /* Pod popupy (ResponsiveDialog z-index 1200+) i pod NavDrawer. */
    z-index: 900;
    background: #263238;
    border-radius: 16px 16px 0 0;
    box-shadow: 0 -2px 10px rgba(0,0,0,0.4);
    /* PEVNÁ výška (ne max-height), aby žádná záložka neměla viditelně menší
       panel než záložka s týmem. */
    height: 46px;
    display: flex;
    flex-direction: column;
    transition: height 0.22s ease;
}
/* Výška spočítaná z reálných rozměrů prvků (ne vh - mobily jsou různě
   velké). 46px úchyt (.team-sheet-handle) + 6x řádek pokémona (46px kruh +
   6px padding nahoru/dolů = 58px/řádek) + 12px spodní padding .team-sheet-body. */
.team-sheet--expanded {
    height: calc(46px + 6 * 58px + 12px);
}
/* Během živého tažení vypíná CSS transition height, aby výška sledovala
   prst/kurzor bez zpoždění - vrací se zpět, jakmile isDragging sklopí na
   false (viz dragInlineStyle/pointerup). */
.team-sheet.team-sheet--dragging {
    transition: none;
}
.team-sheet-handle {
    flex: none;
    height: 46px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    /* Bez tohodle prohlížeč na mobilu interpretuje svislý tah jako scroll
       .v-main__wrap a capturovaný pointer dostane pointercancel v půlce gesta. */
    touch-action: none;
}
.team-sheet-handle-bar {
    position: absolute;
    top: 6px;
    left: 50%;
    transform: translateX(-50%);
    width: 40px;
    height: 4px;
    border-radius: 2px;
    background: rgba(255,255,255,0.4);
}
.team-sheet-peek {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 6px;
    /* Bez overflow:hidden - sprite vědomě vykukuje přes .team-sheet-peek-ring
       (viz .team-sheet-peek-sprite níže). */
}
/* Wrapper s conic-gradient pozadim (viz hpRingGradient v methods) simuluje
   poměrový 2-barevný border, který čistý CSS border neumí. position:relative
   kotví absolutně pozicovaný sprite (viz .team-sheet-peek-sprite níže). */
.team-sheet-peek-ring {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    padding: 3px;
    box-sizing: border-box;
    flex: none;
    position: relative;
}
/* Kulaté pozadí mezi koláčovým prstencem (hpRingGradient) a spritem
   pokémona - vyplňuje vnitřní plochu prstence, barva jde přes inline
   :style (hpBgColor v methods) - zlatá normálně, červená při endangered. */
.team-sheet-peek-bg {
    width: 100%;
    height: 100%;
    border-radius: 50%;
}
/* Sprite mimo v-avatar (ten by ho ořízl na kruh) - absolutně centrovaný
   přes prstenec/kouli, větší než ony (44px vs 36px ring), vědomě vykukuje
   před nimi. pointer-events:none, ať nepřekáží kliku na handle pod ním. */
.team-sheet-peek-sprite {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 44px;
    height: 44px;
    transform: translate(-50%, -50%);
    z-index: 2;
    pointer-events: none;
}
.team-sheet-peek-empty {
    color: rgba(255,255,255,0.7);
    font-size: 13px;
}
.team-sheet-body {
    /* flex-grow, aby výška vycházela z dostupného prostoru v .team-sheet
       sloupci, ne kruhově z toho, kolik řádků se zrovna vykreslilo -
       clientHeight se pak měří proti skutečné dostupné ploše (viz
       measureFittingRowCount v methods). min-height:0 dovolí zmenšit
       položku pod její "natural" obsahovou výšku. */
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: 0 12px 12px 12px;
    /* Zabrání "protečení" scrollu do nadřazené stránky (mobilní
       pull-to-refresh), když uživatel omylem tahne tělo místo handle. */
    overscroll-behavior-y: contain;
}
/* min-height:100% necha obsah rust nad dostupnou vysku .team-sheet-body;
   margin-top:auto na .team-sheet-page-nav (viz TeamSheetPagerNav.vue) pak
   sipky strankovani vzdy dotlaci na spodek, misto aby "skakaly" hned za
   poslednim radkem na kratsich strankach. */
.team-sheet-messages,
.team-sheet-player-messages {
    display: flex;
    flex-direction: column;
    min-height: 100%;
}
/* Drag přes celé tělo jen na tabech bez vlastního scrollování (viz
   TEAM_SHEET_BODY_DRAG_TABS) - pan-x nechá prohlížeč řešit vodorovný
   pan/scroll sám, jen zabrání vzít svislý tah jako scroll/refresh dřív,
   než ho stihne zpracovat pointer handler. */
.team-sheet-body--draggable {
    touch-action: pan-x;
}
.team-sheet-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 0;
}
/* Stejná prstenec+koule vrstva jako .team-sheet-peek-ring/-bg výše, jen
   ve větším rozkliknutém řádku. */
.team-sheet-row-ring {
    width: 46px;
    height: 46px;
    border-radius: 50%;
    padding: 3px;
    box-sizing: border-box;
    flex: none;
    position: relative;
    cursor: pointer;
}
.team-sheet-row-bg {
    width: 100%;
    height: 100%;
    border-radius: 50%;
}
/* Sprite mimo v-avatar, stejný princip jako .team-sheet-peek-sprite výše,
   jen větší (58px vs 46px ring). */
.team-sheet-row-sprite {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 58px;
    height: 58px;
    transform: translate(-50%, -50%);
    z-index: 2;
    pointer-events: none;
}
/* Stejný vizuál jako .pokemon-detail-level v PokemonDetail.vue
   (radial-gradient koule + mdi-star za číslem), jen natvrdo 46px místo
   zoom proměnných - tady není potřeba responzivní škálování jako u
   detailní karty pokémona v boji. Jeden SVG (kruh/hvezda/cislo ve stejnem
   viewBoxu, zadani uzivatele 2026-08-28, prevzato z PokemonPickerCard.vue)
   misto CSS pozadi + samostatnych HTML vrstev - viz komentar u sablony. */
.team-sheet-level-badge {
    width: 23px;
    height: 23px;
    border-radius: 50%;
    flex: none;
    box-shadow: 1px 3px 6px rgba(243,224,184,0.6);
}
.team-sheet-level-badge-text {
    fill: #000;
    font-weight: 900;
}
.team-sheet-info {
    /* Kratší HP baner místo roztažení na celou šířku řádku. */
    flex: 0 1 45%;
    min-width: 0;
}
.team-sheet-name {
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 2px;
}
.team-sheet-progress-bar {
    margin: 0;
}
.team-sheet-right {
    /* Čísla + drag-úchyt přibité k pravému okraji řádku, místo natažení
       HP baneru na jejich místo (viz .team-sheet-info flex výše). */
    margin-left: auto;
    flex: none;
    display: flex;
    align-items: center;
    gap: 6px;
}
.team-sheet-right-stats {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 1px;
}
.team-sheet-exp-text {
    flex: none;
    display: flex;
    align-items: center;
    gap: 2px;
    color: #fff;
    font-size: 15px;
    font-weight: 600;
    min-width: 48px;
    justify-content: flex-end;
}
.team-sheet-drag-handle {
    cursor: grab;
    /* SortableJS potřebuje potlačit výchozí touch-scroll gesto, jinak
       tažení na mobilu koliduje se scrollem stránky. */
    touch-action: none;
}
.sortable-ghost {
    opacity: 0.35;
}
.team-sheet-tabs-row {
    display: flex;
    align-items: center;
    gap: 10px;
}
.team-sheet-tabs-drag {
    display: flex;
    align-items: center;
    gap: 10px;
}
.team-sheet-tab-icon {
    /* Explicitní pojistka pro SortableJS drag - kdyby se element někdy
       přemístil mimo .team-sheet-handle, ať se touch-scroll na něm
       nezačne prát s dragem. */
    touch-action: none;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    opacity: 0.5;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
}
.team-sheet-tab-icon--active {
    opacity: 1;
    background: rgba(255,255,255,0.12);
}
.team-sheet-tab-icon--evolve {
    opacity: 1;
    background: rgba(255,215,0,0.35);
}
.team-sheet-peek-message {
    color: rgba(255,255,255,0.9);
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1 1 auto;
    min-width: 0;
}
/* Pevná výška CELKEM (Vuetify 2 globálně nastavuje box-sizing:border-box),
   musí zůstat synchronní s TEAM_SHEET_MESSAGE_ROW_HEIGHT v <script> -
   measureFittingRowCount z ní počítá, kolik řádků se vejde bez scrollu. */
.team-sheet-message-row {
    height: 29px;
}
.team-sheet-message-time {
    flex: none;
    color: rgba(255,255,255,0.6);
    font-size: 12px;
    font-weight: 600;
    min-width: 40px;
}
.team-sheet-message-text {
    color: #fff;
    font-size: 13px;
    flex: 1 1 auto;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
/* ".team-sheet-handle " prefix navíc zvyšuje specificitu nad Vuetify
   ".v-icon.v-icon" (ta natvrdo dává position:relative) - bez něj by
   position:absolute tady prohrálo a vůbec se neuplatnilo. Stejný vzor jako
   .fight-report-trainer-mini__level-star v FightReport.vue. */
.team-sheet-handle .team-sheet-peek-unread-icon {
    position: absolute;
    top: 2px;
    /* Zprávy (mdi-email) blíž středu lišty, sword-cross (aktivní PvP
       souboj) blíž kraji - viz --pvp modifikátor níže. */
    right: 34px;
}
.team-sheet-handle .team-sheet-peek-unread-icon--pvp {
    right: 10px;
}
.team-sheet-search-field {
    margin-bottom: 6px;
    /* Vuetify dává .v-input globálně "flex: 1 1 auto" - v našem vlastním
       flex sloupci (.team-sheet-player-messages) by se tím pole na krátké
       stránce natáhlo a sežralo volnou plochu místo aby ji margin-top:auto
       nechal šipkám (TeamSheetPagerNav.vue). flex:none vrací poli jeho
       přirozenou výšku. */
    flex: none;
}
.team-sheet-conv-row {
    cursor: pointer;
}
/* Match, který je právě otevřený na PvpBattleScreen, je v listu vyznačený.
   Stejný odstín jako .team-sheet-tab-icon--active (rgba bílá, ne zlatá -
   zlatá už znamená "je na tobě tah", tohle je jiný význam). */
.team-sheet-conv-row--active {
    background: rgba(255, 255, 255, 0.12);
    border-radius: 8px;
}
/* Na rozdíl od pokémon "peek" prstenců (ty vědomě NEOŘÍZAVAJÍ sprite, viz
   .team-sheet-peek-sprite výše) tady jde o skutečný kruhový ořez
   (overflow:hidden) - trenérský sprite (Trainer::$img, celá postava) se
   přes object-position:top zobrazí jen horní půlkou. */
.team-sheet-conv-avatar-wrap {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    overflow: hidden;
    flex: none;
    background: rgba(255,255,255,0.12);
}
.team-sheet-conv-avatar {
    width: 100%;
    height: 100%;
}
/* Samostatný outer wrapper BEZ overflow:hidden, protože
   .team-sheet-conv-avatar-wrap má overflow:hidden + border-radius:50%
   kvůli kruhovému ořezu avataru - kdyby tečka byla přímý potomek tohoto
   wrapperu, byla by z větší části oříznutá. */
.team-sheet-conv-avatar-outer {
    position: relative;
    flex: none;
}
.team-sheet-conv-online-dot {
    position: absolute;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #4CAF50;
    border: 2px solid rgba(0,0,0,0.6);
    right: -1px;
    bottom: -1px;
}
.team-sheet-conv-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    flex: 1 1 auto;
}
.team-sheet-conv-name {
    color: #fff;
    font-size: 13px;
    font-weight: 600;
}
.team-sheet-conv-preview {
    color: rgba(255,255,255,0.75);
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
}
/* `unread` chodí už z GetPlayerConversationsAction na BE, tady se jen
   vizuálně promítá (stejně jako v peek liště). */
.team-sheet-conv-row--unread .team-sheet-conv-preview {
    color: #fff;
    font-weight: 700;
}
.team-sheet-trainer {
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.team-sheet-trainer-info-table {
    border-collapse: collapse;
    color: #fff;
    font-size: 13px;
}
.team-sheet-trainer-info-table td {
    padding: 2px 8px 2px 0;
}
.team-sheet-trainer-info-table td:first-child {
    color: rgba(255,255,255,0.6);
}
.team-sheet-trainer-info-icon {
    margin-right: 4px;
}
.team-sheet-trainer-title {
    color: #FFD700;
    font-size: 14px;
    font-weight: 700;
}
.team-sheet-trainer-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}
.team-sheet-trainer-badge-icon {
    width: 28px;
    height: 28px;
    flex: none;
}
.team-sheet-trainer-achievements {
    display: flex;
    flex-direction: column;
    gap: 4px;
}
.team-sheet-trainer-achieve-row {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 8px;
    border-radius: 6px;
    cursor: pointer;
    background-color: rgba(255,255,255,0.06);
    -webkit-tap-highlight-color: transparent;
}
.team-sheet-trainer-achieve-row--active {
    background-color: rgba(255,255,255,0.16);
}
.team-sheet-trainer-achieve-name {
    color: #fff;
    font-size: 13px;
    flex: 1 1 auto;
}
.team-sheet-trainer-achieve-stars {
    flex: none;
    white-space: nowrap;
}
.team-sheet-trainer-achieve-value {
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    min-width: 20px;
    text-align: right;
    flex: none;
}
.team-sheet-trainer-skills-summary {
    color: rgba(255,255,255,0.8);
    font-size: 13px;
    padding: 4px 8px 8px;
}
.team-sheet-trainer-skills {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1 1 auto;
    min-width: 0;
}
.team-sheet-trainer-skill-row {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 8px;
    border-radius: 6px;
    cursor: pointer;
    background-color: rgba(255,255,255,0.06);
    -webkit-tap-highlight-color: transparent;
}
.team-sheet-trainer-skill-row--active {
    background-color: rgba(255,255,255,0.16);
}
.team-sheet-trainer-skill-name {
    color: #fff;
    font-size: 13px;
    flex: 1 1 auto;
}
.team-sheet-trainer-skill-buy {
    flex: none;
}
/* Zrcadlení pro praváky (flags.rightHanded) - výchozí (flag off) je Buy
   vpravo za hodnotou skillu, daleko od palce leváka. Stejný princip jako
   TrainerBag.vue .skill-buy-row__action--left. */
.team-sheet-trainer-skill-buy--left {
    order: -1;
}
.team-sheet-trainer-skill-value {
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    min-width: 20px;
    text-align: right;
}
</style>
